import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ChevronDown, Eye, Phone, Copy, Truck, Filter, Download,
  RefreshCw, User, Check, FileText, MessageCircle, Plus, X, Layout, Calendar, Tag
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi, employeesApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import { Modal, message } from 'antd';
import { usePermissions, P } from '../hooks/Usepermissions';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import { formatDate } from '../utils/helpers';


// Auth Helper
const getAuthUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch { return null; }
};

// Courier tracking URLs
const COURIER_TRACKING_URLS = {
  'E-kart': 'https://ekartlogistics.com/track/',
  'Fship': 'https://www.fship.in/track/',
  'Shadowfax': 'https://tracker.shadowfax.in/#/',
  'Delhivery': 'https://www.delhivery.com/track/package/',
  // Fallback — asli tracking URL backend ke shipment ke `trackUrl` field me aata hai.
  'VELOCITY': 'https://shipfastt.in/track/'
};

// Customer Status Options
const CUSTOMER_STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Convinced to receive', label: 'Convinced to receive' },
  { value: 'Not connected', label: 'Not connected' },
  { value: 'Not interested', label: 'Not interested' },
  { value: 'Others', label: 'Others' },
  { value: 'Update contact number', label: 'Update contact number' }
];

const statusFilterOptions = ['All status', 'NDR', 'Convinced to receive', 'Not connected', 'Not interested', 'Pending'];

// Customer Status Badge
const CustomerStatusBadge = ({ status }) => {
  const { currentTheme } = useTheme();
  const getStatusStyle = (s) => {
    switch (s) {
      case 'Convinced to receive': return { backgroundColor: `${currentTheme.color}1A`, color: currentTheme.color, borderColor: `${currentTheme.color}33` };
      case 'Delivered': return { backgroundColor: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' };
      case 'Not connected': return { backgroundColor: '#fff7ed', color: '#ea580c', borderColor: '#ffedd5' };
      case 'Not interested': return { backgroundColor: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' };
      default: return { backgroundColor: '#f9fafb', color: '#4b5563', borderColor: '#e5e7eb' };
    }
  };
  const style = getStatusStyle(status);
  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border`} style={style}>
      {status || 'Pending'}
    </span>
  );
};

function NDRRequest() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const location = useLocation();
  const { has, isAdmin } = usePermissions();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const tableContainerRef = useRef(null);

  const [activeFilters, setActiveFilters] = useState({
    search: '', orders: 'All Orders', courier: 'All courier',
    courierPartner: 'All Partners', date: '', agent: 'All Agents',
    category: 'All Categories', status: 'All status', paymentMode: 'All Modes',
    ndrBy: 'Select User', campaign: 'All Campaigns', action: 'Select Action',
    customerStatus: 'All Status'
  });
  const [tempFilters, setTempFilters] = useState(activeFilters);

  const courierOptions = ['All courier', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery', { label: 'Velocity', value: 'VELOCITY' }];
  const partnerOptions = ['All Partners', 'E-kart', 'Fship', 'Shadowfax', 'Delhivery', { label: 'Velocity', value: 'VELOCITY' }];
  const ordersPerPageOptions = [10, 20, 50, 100];

  // Permission-based flags
  const canAssign = has('NDR_REQUEST_ASSIGN');
  const canExport = has('NDR_REQUEST_EXPORT');
  const canFilter = has('NDR_REQUEST_FILTER');

  useEffect(() => {
    const user = getAuthUser();
    setCurrentUser(user);
    setUserLoaded(true);
    if (user) console.log('Logged in user:', user);
    if (user && (user.role === 'ADMIN' || isAdmin)) fetchEmployees();
  }, []);

  useEffect(() => {
    if (isFilterModalOpen) setTempFilters(activeFilters);
  }, [isFilterModalOpen, activeFilters]);

  useEffect(() => {
    if (userLoaded) fetchNDRRequestOrders();
  }, [currentPage, ordersPerPage, activeFilters, userLoaded]);

  const fetchEmployees = async () => {
    try {
      const response = await ordersApi.getNDREmployees();
      if (Array.isArray(response)) setEmployees(response);
      else if (response?.data) setEmployees(response.data);
    } catch (error) { console.error('Error fetching NDR employees:', error); }
  };

  const fetchNDRRequestOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeFilters.courier !== 'All courier') params.courier = activeFilters.courier;
      if (activeFilters.courierPartner !== 'All Partners') params.courierPartner = activeFilters.courierPartner;
      if (activeFilters.orders !== 'All Orders') params.ordersType = activeFilters.orders;
      if (activeFilters.search?.trim()) params.searchTerm = activeFilters.search.trim();
      if (activeFilters.status !== 'All status') params.status = activeFilters.status;
      if (activeFilters.customerStatus !== 'All Status') params.customerStatus = activeFilters.customerStatus;
      if (activeFilters.date) params.date = activeFilters.date;
      if (activeFilters.agent !== 'All Agents') {
        const emp = employees.find(e => e.name === activeFilters.agent);
        if (emp) params.employeeId = emp.id;
        else params.agent = activeFilters.agent;
      }
      if (activeFilters.category !== 'All Categories') params.category = activeFilters.category;
      if (activeFilters.paymentMode !== 'All Modes') params.paymentMode = activeFilters.paymentMode;
      if (activeFilters.ndrBy !== 'Select User') params.ndrBy = activeFilters.ndrBy;
      if (activeFilters.campaign !== 'All Campaigns') params.campaign = activeFilters.campaign;
      if (currentUser && currentUser.role !== 'ADMIN') params.employeeId = currentUser.id;

      console.log('currentUser.role:', currentUser?.role);
      console.log('Fetching NDR Request orders with params:', params);

      const response = await ordersApi.getNDRRequests(params);
      console.log('NDR Request API Response:', response);

      let ordersData = [], total = 0;
      if (response?.data) { ordersData = response.data; total = response.total || response.data.length; }
      else if (response?.content) { ordersData = response.content; total = response.totalElements || response.content.length; }
      else if (Array.isArray(response)) { ordersData = response; total = response.length; }

      const mappedOrders = ordersData.map((order, index) => ({
        id: order.id || index + 1,
        orderId: order.orderId || `OD${order.id}`,
        orderDate: (order.orderDate || order.createdAt) ? formatDate(order.orderDate || order.createdAt) : '-',
        orderBy: order.orderBy || order.createdByName || order.employee?.name || '-',
        orderByEmp: order.orderByEmp || order.employeeId || order.employee?.employeeId || '-',
        customerName: order.customerName || order.lead?.customerName || '-',
        phone: order.contactNumber || order.phone || order.lead?.contactNumber || '-',
        whatsappVerified: order.whatsappVerified || order.isWhatsappVerified || false,
        product: order.productName || order.product?.name || order.orderItems?.[0]?.productName || '-',
        category: order.category || order.product?.category || '-',
        amount: order.grandTotal || order.amount || order.restAmount || 0,
        orderCampaign: order.orderCampaign || order.campaign || order.leadSource || '-',
        status: order.status || order.ndrStatus || 'Undelivered',
        ndrBy: order.assignedTo || order.ndrBy || '-',
        ndrByEmp: order.assignedToEmpId || order.ndrByEmp || '-',
        customerStatus: order.customerStatus || 'Convinced to receive',
        cPartner: order.cPartner || order.courierPartner || order.courierName || '-',
        courier: order.courierName || order.courier || '-',
        awbNumber: order.trackingNumber || order.awbNumber || '-',
      }));

      setOrders(mappedOrders);
      setTotalOrders(total);
      setSelectedOrders([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching NDR Request orders:', error);
      setOrders([]); setTotalOrders(0);
    } finally { setLoading(false); }
  };

  const handleExport = () => {
    if (orders.length === 0) {
      message.warning({
        content: "No data available to export",
        style: { marginTop: '10vh' }
      });
      return;
    }

    try {
      // Define CSV Headers specific to NDR
      const headers = [
        "Order ID", "Order Date", "Order By", "Customer Name", "Phone", 
        "Product", "Category", "Amount", "Campaign", "NDR Status", 
        "NDR By", "Customer Status", "Courier", "AWB Number"
      ];

      // Map data to rows
      const rows = orders.map(order => [
        order.orderId,
        `"${order.orderDate}"`,
        `"${order.orderBy}"`,
        `"${order.customerName}"`,
        order.phone,
        `"${order.product}"`, 
        order.category,
        order.amount,
        `"${order.orderCampaign}"`,
        order.status,
        `"${order.ndrBy}"`,
        `"${order.customerStatus}"`,
        order.courier,
        order.awbNumber
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.join(","))
      ].join("\n");

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `NDR_Requests_${new Date().toLocaleDateString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success({
        content: "NDR report downloaded successfully!",
        style: { marginTop: '10vh' }
      });
    } catch (error) {
      message.error("Failed to export NDR report");
      console.error(error);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedOrders([]);
    else setSelectedOrders(orders.map(o => o.id));
    setSelectAll(!selectAll);
  };

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    else setSelectedOrders([...selectedOrders, orderId]);
  };

  const handleAssignOrders = async () => {
    if (!selectedEmployee) { Modal.warning({ title: 'Selection Required', content: 'Please select an employee' }); return; }
    if (selectedOrders.length === 0) { Modal.warning({ title: 'Selection Required', content: 'Please select at least one order' }); return; }
    try {
      const payload = {
        orderIds: selectedOrders,
        assignedToEmployeeId: selectedEmployee.id,
        assignmentNotes: `Assigned by ${currentUser?.name || 'Admin'} via NDR Request`
      };
      const response = await ordersApi.assignNDROrders(payload);
      if (response?.successCount !== undefined) {
        Modal.success({ title: 'Success', content: `Successfully assigned ${response.successCount} orders to ${selectedEmployee.name}` });
      } else {
        Modal.success({ title: 'Success', content: `Successfully assigned ${selectedOrders.length} orders to ${selectedEmployee.name}` });
      }
      setSelectedOrders([]); setSelectAll(false); setSelectedEmployee(null);
      fetchNDRRequestOrders();
    } catch (error) {
      console.error('Error assigning orders:', error);
      Modal.error({ title: 'Assignment Failed', content: 'Failed to assign orders: ' + (error.message || 'Unknown error') });
    }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);
  const handleViewOrder = (orderId) => navigate(`/orders/amount-decrease/${orderId}`, { state: { activeTab: 'NDR_REQUEST', from: location.pathname } });
  const handleRefresh = () => fetchNDRRequestOrders();

  const totalPages = Math.ceil(totalOrders / ordersPerPage);
  const showingFrom = totalOrders > 0 ? (currentPage - 1) * ordersPerPage + 1 : 0;
  const showingTo = Math.min(currentPage * ordersPerPage, totalOrders);

  const getPageNumbers = () => {
    const pages = [], max = 5;
    if (totalPages <= max) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3) { for (let i = 1; i <= max; i++) pages.push(i); }
    else if (currentPage >= totalPages - 2) { for (let i = totalPages - max + 1; i <= totalPages; i++) pages.push(i); }
    else { for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i); }
    return pages;
  };

  const resetFilters = {
    search: '', orders: 'All Orders', courier: 'All courier', courierPartner: 'All Partners',
    date: '', agent: 'All Agents', category: 'All Categories', status: 'All status',
    paymentMode: 'All Modes', ndrBy: 'Select User', campaign: 'All Campaigns',
    action: 'Select Action', customerStatus: 'All Status'
  };

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="flex flex-col gap-6">

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFilterModalOpen(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] p-4 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-full">
            <div className="bg-white rounded-xl w-full max-w-4xl mx-auto flex flex-col shadow-xl my-8 relative">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
                <h2 className="text-lg font-bold text-white">Filter Orders</h2>
                <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Search</label><ExpandableSearchBar searchTerm={tempFilters.search} onSearchChange={(v) => setTempFilters({ ...tempFilters, search: v })} placeholder="Search..." fullWidth className="w-full" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Orders</label><ThemeSelect value={tempFilters.orders} onChange={(v) => setTempFilters({ ...tempFilters, orders: v })} options={['All Orders', 'Fresh Orders', 'Re-assigned Orders']} className="w-full" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Courier</label><ThemeSelect value={tempFilters.courier} onChange={(v) => setTempFilters({ ...tempFilters, courier: v })} options={courierOptions} className="w-full" icon={Truck} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Courier Partner</label><ThemeSelect value={tempFilters.courierPartner} onChange={(v) => setTempFilters({ ...tempFilters, courierPartner: v })} options={partnerOptions} className="w-full" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Date</label><input type="date" value={tempFilters.date} onChange={(e) => setTempFilters({ ...tempFilters, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Agents</label><ThemeSelect value={tempFilters.agent} onChange={(v) => setTempFilters({ ...tempFilters, agent: v })} options={['All Agents', ...employees.map(e => e.name)]} className="w-full" icon={User} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Category</label><ThemeSelect value={tempFilters.category} onChange={(v) => setTempFilters({ ...tempFilters, category: v })} options={['All Categories', 'Ayurvedic Men Wellness', 'Ayurvedic', 'Wellness']} className="w-full" icon={Tag} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Status</label><ThemeSelect value={tempFilters.status} onChange={(v) => setTempFilters({ ...tempFilters, status: v })} options={statusFilterOptions} className="w-full" icon={RefreshCw} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label><ThemeSelect value={tempFilters.paymentMode} onChange={(v) => setTempFilters({ ...tempFilters, paymentMode: v })} options={['All Modes', 'COD', 'Prepaid']} className="w-full" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">NDR By</label><ThemeSelect value={tempFilters.ndrBy} onChange={(v) => setTempFilters({ ...tempFilters, ndrBy: v })} options={['Select User', ...employees.map(e => e.name)]} className="w-full" icon={User} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label><ThemeSelect value={tempFilters.campaign} onChange={(v) => setTempFilters({ ...tempFilters, campaign: v })} options={['All Campaigns', 'Campaign 1', 'Campaign 2']} className="w-full" icon={Layout} /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Actions</label><ThemeSelect value={tempFilters.action} onChange={(v) => setTempFilters({ ...tempFilters, action: v })} options={['Select Action', 'Export', 'Print', 'Delete']} className="w-full" /></div>
                  <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-2">Customer Status</label><ThemeSelect value={tempFilters.customerStatus} onChange={(v) => setTempFilters({ ...tempFilters, customerStatus: v })} options={['All Status', ...CUSTOMER_STATUS_OPTIONS.map(o => o.label).filter(l => l !== 'Select Status')]} className="w-full" /></div>
                </div>
              </div>
              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 rounded-b-xl">
                <button onClick={() => setTempFilters(resetFilters)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Reset</button>
                <button onClick={() => { setActiveFilters(tempFilters); setCurrentPage(1); setIsFilterModalOpen(false); }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: currentTheme.color }}>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>NDR Request</h1>
            <span className="text-gray-500 text-lg desktop:text-2xl">({totalOrders.toLocaleString()})</span>
          </div>
          <div className="flex items-center space-x-3">
            {currentUser && (
              <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 desktop:px-4 desktop:py-3 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
                <div className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-teal-100'}`}>
                  <User className={`w-4 h-4 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-teal-600'}`} />
                </div>
                <div className="text-sm desktop:text-base">
                  <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{currentUser.name}</p>
                  <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{currentUser.role}</p>
                </div>
              </div>
            )}
            <button onClick={handleRefresh} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
              <RefreshCw className={`w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {/* Filter button — NDR_REQUEST_FILTER */}
            {canFilter && (
              <button onClick={() => setIsFilterModalOpen(true)} className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/5' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500" />
              </button>
            )}
            {/* Export button — NDR_REQUEST_EXPORT */}
            {canExport && (
              <ExcelExportButton
                onClick={handleExport}
                isDownloading={loading}
              />
            )}
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex items-center space-x-4 mb-6 flex-wrap gap-y-3">
          <ExpandableSearchBar searchTerm={activeFilters.search} onSearchChange={(v) => { setActiveFilters(p => ({ ...p, search: v })); setCurrentPage(1); }} placeholder="Search by lead id or customer name" />
          <ThemeSelect value={`${ordersPerPage} Orders`} onChange={(v) => { setOrdersPerPage(parseInt(v)); setCurrentPage(1); }} options={ordersPerPageOptions.map(o => `${o} Orders`)} className="w-44 desktop:w-60" />
          <ThemeSelect value={activeFilters.courier} onChange={(v) => { setActiveFilters(p => ({ ...p, courier: v })); setCurrentPage(1); }} options={courierOptions} icon={Truck} className="w-48 desktop:w-64" />
          <ThemeSelect value={activeFilters.courierPartner} onChange={(v) => { setActiveFilters(p => ({ ...p, courierPartner: v })); setCurrentPage(1); }} options={partnerOptions} icon={User} className="w-48 desktop:w-64" />
          <ThemeSelect value={activeFilters.status} onChange={(v) => { setActiveFilters(p => ({ ...p, status: v })); setCurrentPage(1); }} options={statusFilterOptions} icon={RefreshCw} className="w-48 desktop:w-64" />
        </div>

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${canAssign ? 'flex-1' : 'w-full'} ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
            <div ref={tableContainerRef} className="overflow-x-auto" onScroll={(e) => setScrollPosition(e.target.scrollLeft)}>
              <table className="w-full min-w-[1600px]">
                <thead className="text-white" style={{ background: currentTheme.color }}>
                  <tr>
                    {/* Checkbox column — canAssign */}
                    {canAssign && (
                      <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center w-12">
                        <button onClick={handleSelectAll}
                          className={`w-6 h-6 desktop:w-8 desktop:h-8 rounded border-2 flex items-center justify-center transition-colors`}
                          style={{
                            backgroundColor: selectAll ? 'white' : 'transparent',
                            borderColor: selectAll ? 'white' : 'rgba(255, 255, 255, 0.5)'
                          }}>
                          {selectAll && <Check className="w-4 h-4 desktop:w-6 desktop:h-6" style={{ color: currentTheme.color }} />}
                        </button>
                      </th>
                    )}
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Order ID</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Order By</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Product</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Rest Amt.</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Order Campaign</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">NDR By</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Customer Status</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">C.Partner</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Courier</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">AWB No.</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-xs desktop:text-base font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {loading ? (
                    <tr><td colSpan={canAssign ? 16 : 15} className="px-4 py-12 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}></div>
                        <span className="text-gray-500">Loading NDR requests...</span>
                      </div>
                    </td></tr>
                  ) : orders.length === 0 ? (
                    <tr><td colSpan={canAssign ? 16 : 15} className="px-4 py-12 text-center text-gray-500">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" /><p>No NDR requests found</p>
                    </td></tr>
                  ) : orders.map((order, idx) => (
                    <tr key={order.id} className={`transition-colors ${isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50'}`}>

                      {/* Row checkbox — canAssign */}
                      {canAssign && (
                        <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                          <button onClick={() => handleSelectOrder(order.id)}
                            className={`w-6 h-6 desktop:w-8 desktop:h-8 rounded-full border-2 flex items-center justify-center transition-colors`}
                            style={{
                              backgroundColor: selectedOrders.includes(order.id) ? currentTheme.color : 'transparent',
                              borderColor: selectedOrders.includes(order.id) ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.25)' : '#d1d5db')
                            }}
                            onMouseEnter={(e) => { if(!selectedOrders.includes(order.id)) e.currentTarget.style.borderColor = currentTheme.color; }}
                            onMouseLeave={(e) => { if(!selectedOrders.includes(order.id)) e.currentTarget.style.borderColor = '#d1d5db'; }}
                          >
                            {selectedOrders.includes(order.id) && <Check className="w-3 h-3 desktop:w-5 desktop:h-5 text-white" />}
                          </button>
                        </td>
                      )}

                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <button onClick={() => handleViewOrder(order.id)} className="text-sm desktop:text-base font-medium hover:underline" style={{ color: currentTheme.color }}>
                          {order.orderId}
                        </button>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">{order.orderDate}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'} truncate max-w-[120px]`}>{order.orderBy}</span>
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.orderByEmp}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customerName}</span>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 desktop:w-4 desktop:h-4 text-gray-500" />
                            <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.phone}</span>
                            {order.whatsappVerified && (
                              <div className="w-4 h-4 desktop:w-5 desktop:h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <MessageCircle className="w-2.5 h-2.5 desktop:w-3 desktop:h-3 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block max-w-[150px]`} title={order.product}>{order.product}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{order.category}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className="text-sm desktop:text-base font-semibold" style={{ color: currentTheme.color }}>₹ {order.amount.toLocaleString()}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.orderCampaign}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.status}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'} truncate max-w-[100px]`}>{order.ndrBy}</span>
                          <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {order.ndrByEmp}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><CustomerStatusBadge status={order.customerStatus} /></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.cPartner}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5"><span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{order.courier}</span></td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg transition-all ${isDark ? 'bg-white/5 border border-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}>
                          <a href={`${COURIER_TRACKING_URLS[order.courier] || '#'}${order.awbNumber}`} target="_blank" rel="noopener noreferrer" className={`text-sm desktop:text-base font-mono font-bold ${isDark ? 'text-white' : 'text-teal-600'} hover:underline`}>{order.awbNumber}</a>
                          <button onClick={() => copyToClipboard(order.awbNumber)} className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-white'}`} title="Copy AWB"><Copy className={`w-3.5 h-3.5 desktop:w-4 desktop:h-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                        <button onClick={() => handleViewOrder(order.id)}
                          className={`p-2 desktop:p-3 rounded-xl transition-all ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                          style={{ color: currentTheme.color }} title="View Details">
                          <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination 
              currentPage={currentPage}
              totalItems={totalOrders}
              itemsPerPage={ordersPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          {/* Assign Panel — canAssign */}
          {canAssign && (
            <div className={`w-72 desktop:w-96 rounded-xl shadow-sm border h-fit flex-shrink-0 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
              <div className={`p-4 desktop:p-6 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                <h3 className={`text-lg desktop:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Assign to</h3>
              </div>
              <div className="p-4 desktop:p-6 max-h-[500px] desktop:max-h-[600px] overflow-y-auto">
                <div className="space-y-2 desktop:space-y-4">
                  {employees.map(employee => (
                    <button key={employee.id} onClick={() => setSelectedEmployee(employee)}
                      className={`w-full flex items-center space-x-3 p-3 desktop:p-4 rounded-xl transition-all border-2 ${selectedEmployee?.id === employee.id ? '' : (isDark ? 'hover:bg-white/5 border-transparent' : 'hover:bg-gray-50 border-transparent')}`}
                      style={{
                        backgroundColor: selectedEmployee?.id === employee.id ? currentTheme.color + '1A' : undefined,
                        borderColor: selectedEmployee?.id === employee.id ? currentTheme.color : 'transparent'
                      }}>
                      <div className="w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                        style={{ backgroundColor: selectedEmployee?.id === employee.id ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB') }}>
                        {selectedEmployee?.id === employee.id
                          ? <Check className="w-4 h-4 desktop:w-5 desktop:h-5 text-white" />
                          : <User className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />}
                      </div>
                      <div className="text-left">
                        <p className={`text-sm desktop:text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{employee.name}</p>
                        <p className={`text-xs desktop:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Emp: {employee.employeeId || employee.id}</p>
                      </div>
                    </button>
                  ))}
                  {employees.length === 0 && <p className={`text-sm desktop:text-base text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No employees found</p>}
                </div>
              </div>
              <div className={`p-4 desktop:p-6 border-t rounded-b-xl ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                <button onClick={handleAssignOrders} disabled={!selectedEmployee || selectedOrders.length === 0}
                  className="w-full py-3 desktop:py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed text-white"
                  style={{
                    backgroundColor: currentTheme.color,
                    boxShadow: selectedEmployee && selectedOrders.length > 0 ? `0 8px 20px -5px ${currentTheme.color}60` : 'none'
                  }}>
                  Assign {selectedOrders.length > 0 ? `(${selectedOrders.length})` : ''}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default NDRRequest;